<template>
  <div>
    <div
      v-if="error"
      class="settings-box"
    >
      <span
        class="text-danger small"
      >
        {{ $t('error.settingsUnavailable') }}
      </span>
    </div>
    <div
      class="settings-box position-relative"
      :class="{ disabled: error }"
    >
      <div class="section-title">
        {{ $t('menu.accounting') }}
      </div>
      <SwitchWithDescription
        class="mt-2"
        :title="$t('accounting.settings.module')"
        :description="$t('accounting.settings.enabledInfo')"
        :pending="pending || error"
        :value="enabled"
        @switch="updateEnabled($event.toString())"
      />
    </div>
    <div
      class="settings-box position-relative"
      :class="{ disabled: error }"
    >
      <div class="section-title">
        Ostrzeżenie o braku paragonów
      </div>
      <SwitchWithDescription
        class="mt-2"
        title="Ostrzeżenie o braku paragonów"
        description="Pagaspot będzie monitorował dodawane wpłaty, jeśli fiskalizacja zostanie wstrzymana
        poinformujemy o tym wiadomością email"
        :pending="pending || error"
        :value="autoPrintWarning"
        @switch="updateWarning($event)"
      />
      <div v-if="autoPrintWarning">
        <div class="d-flex align-items-center mt-4">
          <div class="font-weight-bold mb-0">
            Wyślij ostrzeżenie do
          </div>
          <div class="ml-3">
            <div
              v-if="!editWarningEmails"
              class="sort-option-selected page-setting d-flex align-items-center justify-content-center"
              @click="editWarningEmails = true"
            >
              <i class="fa-solid fa-pen-to-square" />
            </div>
            <div v-else>
              <SaveButton
                :pending="warningPending"
                @save="updateWarning(true)"
                @cancel="editWarningEmails = false; init(currentConfig)"
              />
            </div>
          </div>
        </div>
        <div v-if="editWarningEmails">
          <BFormTextarea
            v-model="autoPrintWarningEmails"
            :disabled="error || warningPending"
            :placeholder="$t('accounting.autoPrintWarningEmails')"
            rows="3"
            max-rows="8"
            class="mt-3"
            size="sm"
          />
          <div
            class="text-muted small"
          >
            Wprowadź adresy email w kolejnych wierszach
          </div>
        </div>
        <div v-else>
          <div
            v-for="email in autoPrintWarningEmails.split('\n')"
            :key="email"
          >
            {{ email }}
          </div>
        </div>
      </div>
    </div>
    <div
      class="settings-box position-relative"
      :class="{ disabled: error }"
    >
      <div class="d-flex">
        <div class="section-title mb-0">
          {{ $t('accounting.payeeDetails') }}
        </div>
        <div class="ml-3">
          <div
            v-if="!editPayeeDetails"
            class="sort-option-selected page-setting d-flex align-items-center justify-content-center"
            @click="editPayeeDetails = true"
          >
            <i class="fa-solid fa-pen-to-square" />
          </div>
          <div v-else>
            <SaveButton
              :pending="payeeDetailsPending"
              @save="savePayeeDetails"
              @cancel="editPayeeDetails = false; init(currentConfig)"
            />
          </div>
        </div>
      </div>
      <div v-if="editPayeeDetails">
        <BFormTextarea
          v-model="payeeDetails"
          :disabled="payeeDetailsPending"
          style="width: 500px"
          rows="5"
          :placeholder="$t('accounting.payeeDetails')"
        />

        <div class="mt-2 font-weight-bold">
          {{ $t('accounting.docPrefix') }}
        </div>
        <BFormInput
          v-model="documentPrefix"
          :disabled="payeeDetailsPending"
          style="width: 500px"
          :placeholder="$t('accounting.docPrefix')"
        />

        <!-- <div class="mt-2 font-weight-bold">
          KSEF Token
        </div>
        <BFormInput
          v-model="ksefToken"
          :disabled="payeeDetailsPending"
          style="width: 500px"
          :placeholder="'KSEF Token'"
        /> -->

        <div
          class="mt-2 font-weight-bold"
        >
          {{ $t('accounting.docType') }}
        </div>
        <BoxSelect
          style="width: 300px"
          :fields="documentTypes"
          :selected.sync="documentType"
        />
      </div>
      <div
        v-else
      >
        <span style="white-space: pre-wrap;">{{ payeeDetails }}</span>

        <div
          v-if="documentPrefix"
          class="mt-2 font-weight-bold"
        >
          {{ $t('accounting.docPrefix') }}
        </div>
        <span v-if="documentPrefix">{{ documentPrefix }}</span>
        <div
          v-if="ksefToken"
          class="mt-2 font-weight-bold"
        >
          KSEF Token
        </div>
        <span v-if="ksefToken">{{ (ksefToken || '').slice(0, 10) }}...</span>
        <div
          class="mt-2 font-weight-bold"
        >
          {{ $t('accounting.docType') }}
        </div>
        <span>
          <i
            :class="documentTypeInfo.icon"
            class="text-secondary"
          />
          {{ documentTypeInfo.text }}
        </span>
      </div>
    </div>
    <div
      class="settings-box position-relative"
      :class="{ disabled: error }"
    >
      <div class="d-flex">
        <div class="section-title">
          {{ $t('settings.billsCategories') }}
        </div>
        <div class="ml-3">
          <div
            v-if="!categoriesEdit"
            class="sort-option-selected page-setting d-flex align-items-center justify-content-center"
            @click="categoriesEdit = true"
          >
            <i class="fa-solid fa-pen-to-square" />
          </div>
          <div v-else>
            <SaveButton
              :pending="categoriesPending"
              @save="saveCategories"
              @cancel="categoriesEdit = false"
            />
          </div>
        </div>
      </div>

      <CategoriesMultiselect
        style="width: 370px"
        :selected.sync="billingCategories"
        :readonly="!categoriesEdit"
      />
    </div>
    <div
      class="settings-box position-relative"
      :class="{ disabled: error }"
    >
      <div class="d-flex">
        <div class="section-title">
          {{ $t('settings.accounting.billingNoteColumns') }}
        </div>
        <div class="ml-3">
          <div
            v-if="!columnsEdit"
            class="sort-option-selected page-setting d-flex align-items-center justify-content-center"
            @click="columnsEdit = true"
          >
            <i class="fa-solid fa-pen-to-square" />
          </div>
          <div v-else>
            <SaveButton
              :pending="columnsPending"
              @save="saveColumns"
              @cancel="columnsEdit = false"
            />
          </div>
        </div>
      </div>

      <BillingNoteColumnsSummary
        v-if="!columnsEdit"
        v-bind="config"
        style="max-width: 900px;"
      />
      <div v-else>
        <BillingNoteColumnsEdit
          :config="config"
          @update="updatedConfig = $event"
        />
      </div>
    </div>

    <div
      class="settings-box position-relative"
      :class="{ disabled: error }"
    >
      <div class="d-flex">
        <div class="section-title">
          {{ $t('settings.accounting.exportRules') }}
        </div>
        <div class="ml-3">
          <div
            v-if="!exportRulesEdit"
            class="sort-option-selected page-setting d-flex align-items-center justify-content-center"
            @click="exportRulesEdit = true"
          >
            <i class="fa-solid fa-pen-to-square" />
          </div>
          <div v-else>
            <SaveButton
              :pending="exportRulesPending"
              @save="saveExportRules"
              @cancel="exportRulesEdit = false"
            />
          </div>
        </div>
      </div>

      <div
        v-if="!exportRulesEdit"
        style="max-width: 900px;"
      >
        <template v-if="config">
          <div
            v-for="(rule, index) in config.exportRules"
            :key="index"
            class="rule-summary d-flex mb-2"
          >
            <div
              class="font-weight-bold"
              style="min-width: 250px"
            >
              {{ rule.fieldName }}
            </div>
            <div class="money-font rule">
              {{ rule.fieldRule }}
            </div>
          </div>
        </template>
      </div>
      <div v-else>
        <ExportSetup
          v-if="config"
          :config="config"
          @rules="exportRules = $event"
        />
      </div>
    </div>
  </div>
</template>

<script>
import BoxSelect from '@/components/BoxSelect';
import SaveButton from '@/components/SaveButton';
import ExportSetup from '@/components/accounting/ExportSetup';
import documentTypes from '@/components/accounting/document-types';
import CategoriesMultiselect from '@/components/settings/CategoriesMultiselect';
import SwitchWithDescription from '@/components/settings/SwitchWithDescription';
import BillingNoteColumnsEdit from '@/components/settings/accounting/BillingNoteColumnsEdit';
import BillingNoteColumnsSummary from '@/components/settings/accounting/BillingNoteColumnsSummary';
import { mapActions } from 'vuex';

export default {
  data: () => ({
    error: null,
    enabled: false,
    pending: false,
    config: null,
    updatedConfig: null,
    columnsEdit: false,
    columnsPending: false,
    exportRulesPending: false,
    exportRulesEdit: false,
    categoriesPending: false,
    categoriesEdit: false,
    exportRules: [],
    billingCategories: [],
    payeeDetailsPending: false,
    editPayeeDetails: false,
    payeeDetails: '',
    documentPrefix: '',
    ksefToken: '',
    documentType: 'BillingNote',
    currentConfig: null,
    autoPrintWarning: false,
    autoPrintWarningEmails: '',
    editWarningEmails: false,
    warningPending: false,
  }),
  components: {
    SwitchWithDescription,
    BillingNoteColumnsSummary,
    BillingNoteColumnsEdit,
    CategoriesMultiselect,
    ExportSetup,
    SaveButton,
    BoxSelect,
  },
  computed: {
    documentTypes() {
      return documentTypes;
    },
    documentTypeInfo() {
      return documentTypes.find((x) => x.key === this.documentType);
    },
  },
  methods: {
    ...mapActions([
      'getAccountingConfig',
      'toggleAccountingEnabled',
      'updateAccountingNoteColumns',
      'updateAccountingExportRules',
      'updateAccountingBillingCategories',
      'updateAccountingPayeeDetails',
      'updateAccountingAutoPrintWarning',
    ]),
    updateWarning(enabled) {
      this.warningPending = true;
      this.autoPrintWarning = enabled;
      this.updateAccountingAutoPrintWarning({
        data: {
          enabled,
          emails: this.autoPrintWarningEmails.trim().split('\n'),
        },
      })
        .then(() => {
          this.editWarningEmails = false;
        })
        .finally(() => {
          this.warningPending = false;
        });
    },
    savePayeeDetails() {
      this.payeeDetailsPending = true;
      this.updateAccountingPayeeDetails({
        data: {
          payeeDetails: this.payeeDetails,
          documentPrefix: this.documentPrefix,
          documentType: this.documentType,
          ksefToken: this.ksefToken,
        },
      })
        .then(() => {
          this.editPayeeDetails = false;
        })
        .finally(() => {
          this.payeeDetailsPending = false;
        });
    },
    saveCategories() {
      this.categoriesPending = true;
      this.updateAccountingBillingCategories({
        data: {
          categoryIds: this.billingCategories,
        },
      })
        .then(() => {
          this.config.billingCategoryIds = this.billingCategories.slice();
          this.categoriesEdit = false;
        })
        .finally(() => {
          this.categoriesPending = false;
        });
    },
    saveExportRules() {
      this.exportRulesPending = true;

      this.updateAccountingExportRules({
        data: {
          exportRules: this.exportRules,
        },
      })
        .then(() => {
          this.exportRulesEdit = false;
          this.config.exportRules = this.exportRules;
        })
        .finally(() => {
          this.exportRulesPending = false;
        });
    },
    saveColumns() {
      this.columnsPending = true;
      this.updateAccountingNoteColumns({
        data: this.updatedConfig,
      })
        .then(() => {
          this.config = this.updatedConfig;
          this.columnsEdit = false;
        })
        .finally(() => {
          this.columnsPending = false;
        });
    },
    updateEnabled(value) {
      this.pending = true;
      this.toggleAccountingEnabled({
        params: {
          query: {
            enabled: value,
          },
        },
      })
        .then(({ data }) => {
          this.init(data);
          this.pending = false;
        })
        .catch(() => {
          this.init(this.config);
          this.pending = false;
        });
    },
    init(data) {
      this.enabled = data.enabled;
      this.config = data;
      this.billingCategories = data.billingCategoryIds;
      this.payeeDetails = data.payeeDetails;
      this.documentPrefix = data.documentPrefix;
      this.ksefToken = data.ksefToken;
      this.documentType = data.documentType;
      this.autoPrintWarning = data.autoPrintWarning;
      this.autoPrintWarningEmails = (data.autoPrintWarningEmails || []).join('\n');
      this.currentConfig = data;
    },
  },
  created() {
    this.$emit('page', 'accounting');

    this.getAccountingConfig()
      .then(({ data }) => {
        this.init(data);
      })
      .catch(() => {
        this.error = true;
      });
  },
};
</script>

<style lang="scss" scoped>
@import '~@/components/payers/style.scss';

.page-setting {
  color: #aaa;
  font-size: 1.2rem;
  padding: 0;
  width: 44px;
  min-height: auto;
  height: 30px;
  text-align: center;
}

.rule-summary {
  background-color: #f6f6f6;
  border-radius: 10px;
  padding: 2px 10px;
  margin-top: 5px;

  .rule {
    margin-left: 10px;
    font-size: 0.9rem;
    white-space: pre-wrap;
  }
}
</style>
